import { useEffect, useState, useCallback } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { toast } from "sonner";
import { HugeiconsIcon } from "@hugeicons/react";
import { UploadIcon, CloseIcon } from "@/components/ui/icons";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { postRequest } from "@/utils/apiHelper";

const MAX_FILE_SIZE = 2 * 1024 * 1024; // 2MB
const ALLOWED_IMAGE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/gif",
  "image/webp",
];

const supportTicketSchema = z.object({
  subject: z
    .string()
    .min(5, "Subject must be at least 5 characters")
    .max(100, "Subject must be less than 100 characters"),
  category: z.enum(["technical", "general", "feature"], {
    message: "Please select a category",
  }),
  message: z
    .string()
    .min(20, "Message must be at least 20 characters")
    .max(1000, "Message must be less than 1000 characters"),
});

type SupportTicketFormData = z.infer<typeof supportTicketSchema>;

interface SupportModalProps {
  open: boolean;
  onClose: () => void;
}

/**
 * SupportModal — formerly a centered Dialog, now a docked slide-up panel
 * anchored to the bottom-right (Intercom-style). Does NOT block the page:
 * users can keep interacting with content behind it; close is explicit
 * via the X button.
 *
 * Same form fields and submit endpoint as the prior version.
 */
export function SupportModal({ open, onClose }: SupportModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);

  const form = useForm<SupportTicketFormData>({
    resolver: zodResolver(supportTicketSchema),
    defaultValues: {
      subject: "",
      category: "general",
      message: "",
    },
  });

  // Reset on close so reopening starts fresh.
  useEffect(() => {
    if (!open) {
      form.reset();
      setImagePreview(null);
      setImageFile(null);
    }
  }, [open, form]);

  // Esc to close, just like a dialog.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const handleImageChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.size > MAX_FILE_SIZE) {
        toast.error("File too large", { description: "Image must be less than 2MB" });
        return;
      }
      if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
        toast.error("Invalid file type", {
          description: "Please upload a JPEG, PNG, GIF, or WebP image",
        });
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result as string);
      reader.readAsDataURL(file);
      setImageFile(file);
    }
  }, []);

  const removeImage = useCallback(() => {
    setImagePreview(null);
    setImageFile(null);
  }, []);

  const onSubmit = useCallback(
    async (data: SupportTicketFormData) => {
      try {
        setIsSubmitting(true);

        let imageData;
        if (imageFile) {
          const base64 = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsDataURL(imageFile);
          });
          imageData = {
            filename: imageFile.name,
            mimetype: imageFile.type,
            encoding: "base64",
            base64: base64.split(",")[1],
          };
        }

        await postRequest(
          "support/submit",
          { ...data, image: imageData },
          false,
          undefined,
          "default",
          undefined,
        );

        toast.success("Support ticket submitted successfully!");
        form.reset();
        setImagePreview(null);
        setImageFile(null);
        onClose();
      } catch (error) {
        console.error("Failed to submit support ticket:", error);
        toast.error("Failed to submit support ticket. Please try again.");
      } finally {
        setIsSubmitting(false);
      }
    },
    [form, imageFile, onClose],
  );

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-label="Submit support ticket"
      className={
        // Anchored bottom-right, same corner as the bubble. Slide-up
        // animation. No backdrop — page stays interactive.
        "fixed bottom-6 right-6 z-50 w-[calc(100vw-3rem)] sm:w-[400px] max-h-[calc(100vh-6rem)] " +
        "flex flex-col rounded-xl border border-border bg-background shadow-2xl " +
        "animate-in fade-in-0 slide-in-from-bottom-4 duration-200"
      }
    >
      {/* Header — same chrome shape as our Dialog header (px-5 py-3.5,
          border-b, min-h-[52px]) so the panel reads as part of the design
          system. */}
      <div className="flex items-center justify-between px-5 py-3.5 border-b border-border min-h-[52px]">
        <h2 className="text-sm font-semibold truncate">
          Submit support ticket
        </h2>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="inline-flex items-center justify-center size-7 rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
        >
          <HugeiconsIcon icon={CloseIcon} size={16} />
        </button>
      </div>

      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="flex flex-col min-h-0 flex-1"
        >
          {/* Scrolling body */}
          <div className="flex flex-col gap-4 px-5 py-4 overflow-y-auto">
            <p className="text-sm text-muted-foreground">
              Need help? Send us a message and we'll get back to you as soon as
              possible.
            </p>

            <FormField
              control={form.control}
              name="subject"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Subject</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Brief description of your issue"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="category"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Category</FormLabel>
                  <Select value={field.value} onValueChange={field.onChange}>
                    <FormControl>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select category" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="general">General</SelectItem>
                      <SelectItem value="technical">Technical</SelectItem>
                      <SelectItem value="feature">Feature request</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="message"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Message</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Describe your issue in detail…"
                      className="min-h-[120px] resize-none"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex flex-col gap-2">
              <FormLabel>Attachment (optional)</FormLabel>
              <Input
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp"
                className="hidden"
                id="image-upload"
                onChange={handleImageChange}
              />
              <label
                htmlFor="image-upload"
                className="cursor-pointer border-dashed flex items-center gap-2 border-2 border-border rounded-lg h-10 px-4 hover:bg-muted/50 w-fit transition-colors"
              >
                <HugeiconsIcon icon={UploadIcon} size={16} />
                <span className="text-sm">Upload image</span>
              </label>

              {imagePreview && (
                <div className="relative w-fit">
                  <img
                    src={imagePreview}
                    alt="Preview"
                    className="w-20 h-20 object-cover rounded-md border border-border"
                  />
                  <Button
                    type="button"
                    variant="destructive"
                    size="icon"
                    className="absolute -top-2 -right-2 rounded-full p-0.5 size-6"
                    onClick={removeImage}
                  >
                    <HugeiconsIcon icon={CloseIcon} size={12} />
                  </Button>
                </div>
              )}
              <p className="text-xs text-muted-foreground">
                Max 2MB. Allowed types: JPEG, PNG, GIF, WebP.
              </p>
            </div>
          </div>

          {/* Footer pinned to bottom of panel */}
          <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-border">
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Submitting…" : "Submit ticket"}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}
